import React, { useEffect } from 'react';
import { Dropdown } from '@allygpo/ally-ux';

import { scenarioActions, useScenarioContext } from '../../context/ScenarioContext';
import { usePracticeData } from '../../hooks/usePracticeData';
import { Tip } from '../ui';

/** Top-bar practice picker. Owns the session's practice selection (previously on Home). */
const PracticePicker: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const { practiceOptions, isLoading, error, orgDropdownLocked } = usePracticeData();
  const account = state.generatorState.form?.logged_in_user ?? '';
  const selected = practiceOptions.find((option) => option.value === account);
  const name = state.generatorState.userName || selected?.label || (isLoading ? 'Loading practice…' : error ? 'Practice unavailable' : 'Select a practice');
  const disabled = isLoading || orgDropdownLocked || !practiceOptions.length;

  // Starts the new practice clean: the previous practice's results, pins and any run still
  // generating its remaining areas don't carry over.
  const selectPractice = (nextAccount: string) => {
    const label = practiceOptions.find((option) => option.value === nextAccount)?.label ?? '';
    dispatch(scenarioActions.switchPractice(nextAccount, label));
  };

  useEffect(() => {
    if (isLoading || !practiceOptions.length) return;
    if (!account) {
      selectPractice(practiceOptions[0].value);
    } else if (!practiceOptions.some((option) => option.value === account)) {
      // The remembered practice isn't one of this user's (assignment removed, or another
      // user on this browser). Everything saved with it belongs to that context, so drop
      // it and start from this user's first practice instead of requesting an account the
      // backend will refuse.
      dispatch(scenarioActions.clearAll());
      selectPractice(practiceOptions[0].value);
    }
  // The session selection is established once practices load, whichever page opens first.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isLoading, practiceOptions]);

  type Option = (typeof practiceOptions)[number];
  const pick = (value: string) => { if (value && value !== account) selectPractice(value); };

  // Ally UX Dropdown with search. The closed field reads "Practice  <name>  <ID>"; child practices sit
  // indented under their corporate account, which is bold.
  return <Tip text={error} position="bottom">
    <Dropdown
      className="siq-practice-picker"
      value={account || null}
      options={practiceOptions}
      optionLabel="label"
      optionValue="value"
      onChange={(event) => pick(String(event.value ?? ''))}
      disabled={disabled}
      invalid={!!error}
      filter
      filterBy="label,value"
      filterPlaceholder="Search by practice name or ID"
      scrollHeight="22.5rem"
      panelClassName="siq-practice-panel"
      // The list opens scrolled just far enough to show the current practice, which can leave a half-hidden row
      // at the top. Put the current practice at the top instead, so every visible row is whole.
      onShow={() => requestAnimationFrame(() => {
        const list = document.querySelector<HTMLElement>('.siq-practice-panel [data-pc-section="wrapper"]');
        const selected = list?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
        if (list && selected) list.scrollTop += selected.getBoundingClientRect().top - list.getBoundingClientRect().top;
      })}
      emptyFilterMessage="No practices match that search."
      aria-label="Practice"
      valueTemplate={() => (
        <span className="flex align-items-center u-gap-2 min-w-0">
          <span className="text-2xs text-secondary flex-none">Practice</span>
          <span className={`text-xs font-semibold text-truncate${error ? ' text-danger' : ''}`}>{name}</span>
          {account && <span className="text-2xs text-secondary flex-none">{account}</span>}
        </span>
      )}
      itemTemplate={(option: Option) => (
        <span className={`flex align-items-center u-gap-3 w-full${option.isChild ? ' u-pl-4' : ''}`}>
          <span className={`flex-1 min-w-0${option.isCorporate || option.value === account ? ' font-semibold' : ''}`}>{option.label}</span>
          <span className="text-2xs text-secondary">{option.value}</span>
        </span>
      )}
    />
  </Tip>;
};

export default PracticePicker;
